import { useSelector, useDispatch } from 'react-redux';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAvailableHeight } from '@commonClient2/hooks';
import { EmptyTableText, ScrollableTabsButtonAuto, WeeklyShiftLineTable } from '@commonClient2/components';
import { ScheduleSource, PeriodType } from '../../../../Enums';
import timeService from '../../../../services/TimeService';
import shiftTemplateSegmentService from '../../../../services/ShiftTemplateSegmentService';
import {
    CHECK_DRAFT_SCHEDULE_ITEM, CHECK_ALL_DRAFT_SCHEDULE_ITEM, CHECK_DRAFT_WORKPLAN_SCHEDULE_ITEM, CHECK_ALL_DRAFT_WORKPLAN_SCHEDULE_ITEM,
} from '../../../store/bidProfileDraft';
import {
    CHECK_SCHEDULE_ITEM, CHECK_ALL_SCHEDULE_ITEM, CHECK_WORKPLAN_SCHEDULE_ITEM, CHECK_ALL_WORKPLAN_SCHEDULE_ITEM,
} from '../../../store/bidProfile';
import './ShiftlineTable.scss';

export const DAY_COLUMNS_COUNT = 7; // TODO - Kath 09/21: get this value from BE
/**
 * @param {{isSummary: boolean, isDisabled: boolean}} props
 */
function ShiftlineTable(props) {
    const { isSummary = false, isDisabled = false } = props;
    const { t } = useTranslation('shiftBidding');
    const ref = useRef(null);
    const tableHeight = useAvailableHeight(ref);
    const heightOffset = 40;
    const workplanHeightOffset = 36;

    const dispatch = useDispatch();
    /** @type {ShiftBidding.ManagerStore} */
    // @ts-ignore
    const { bidProfileDraft, bidProfile } = useSelector((state) => state);
    /**
     * @type {BidProfile.IScheduleTemplateItem[]}
     */
    let csvShiftlines = [];
    /**
     * @type {ApiManager.IWorkplan[]}
     */
    let workplans = [];
    /**
     * @type {BidProfile.ISchedulePatternsShiftTemplateDictionary}
     */
    let shiftsDictionary = {};
    
    /**
     * @param {number} value
     * @param {boolean} checked
     * @param {string} workplanId
     */
    // eslint-disable-next-line no-unused-vars
    let handleCheck = (value, checked, workplanId) => { };

    /**
     * @param {number} _
     * @param {boolean} checked
     * @param {string} workplanId
     */
    // eslint-disable-next-line no-unused-vars
    let handleCheckAll = (_, checked, workplanId) => { };

    /**
     * @type {BiddingDraft.IBidProfileDraft | BidProfile.IBidProfile}
     */
    let profile;
    if (bidProfileDraft.id !== '') {
        profile = bidProfileDraft;
        if (profile.bidProfileInformation.scheduleSource === ScheduleSource.ExcelFile) {
            handleCheck = (value, checked) => {
                dispatch({
                    type: CHECK_DRAFT_SCHEDULE_ITEM,
                    payload: { id: value, checked },
                });
            };

            handleCheckAll = (_, checked) => {
                dispatch({ type: CHECK_ALL_DRAFT_SCHEDULE_ITEM, payload: { checked } });
            };
        } else {
            handleCheck = (value, checked, workplanId) => {
                dispatch({
                    type: CHECK_DRAFT_WORKPLAN_SCHEDULE_ITEM,
                    payload: { id: value, checked, workplanId },
                });
            };

            handleCheckAll = (_, checked, workplanId) => {
                dispatch({ type: CHECK_ALL_DRAFT_WORKPLAN_SCHEDULE_ITEM, payload: { checked, workplanId } });
            };
        }
    } else if (bidProfile.id !== '') {
        profile = bidProfile;
        if (profile.bidProfileInformation.scheduleSource === ScheduleSource.ExcelFile) {
            handleCheck = (value, checked) => {
                dispatch({
                    type: CHECK_SCHEDULE_ITEM,
                    payload: { id: value, checked },
                });
            };

            handleCheckAll = (_, checked) => {
                dispatch({ type: CHECK_ALL_SCHEDULE_ITEM, payload: { checked } });
            };
        } else {
            handleCheck = (value, checked, workplanId) => {
                dispatch({
                    type: CHECK_WORKPLAN_SCHEDULE_ITEM,
                    payload: { id: value, checked, workplanId },
                });
            };

            handleCheckAll = (_, checked, workplanId) => {
                dispatch({ type: CHECK_ALL_WORKPLAN_SCHEDULE_ITEM, payload: { checked, workplanId } });
            };
        }
    }
    // @ts-ignore
    if (profile === undefined) {
        return <></>;
    }

    if (isSummary) {
        if (profile.bidProfileInformation.scheduleSource === ScheduleSource.ExcelFile) {
            csvShiftlines = profile.originalScheduleTemplates.items.filter((i) => i.isSelected);
            shiftsDictionary = profile.originalScheduleTemplates.shifts;
        } else {
            workplans = profile.originalScheduleTemplates.retrievedWorkplans?.workplans.map((x) => ({
                ...x,
                scheduleTemplates: {
                    ...x.scheduleTemplates,
                    items: x.scheduleTemplates.items.filter((i) => i.isSelected),
                },
            })) || [];
            shiftsDictionary = profile.originalScheduleTemplates.retrievedWorkplans?.shifts || {};
        }
    } else if (profile.bidProfileInformation.scheduleSource === ScheduleSource.ExcelFile) {
        csvShiftlines = profile.scheduleTemplates.items;
        shiftsDictionary = profile.scheduleTemplates.shifts;
    } else {
        workplans = profile.scheduleTemplates.retrievedWorkplans?.workplans || [];
        shiftsDictionary = profile.scheduleTemplates.retrievedWorkplans?.shifts || {};
    }

    /**
     * @param {string} id
     * @returns {BidProfile.IShiftTemplate}
     */
    const getShiftTemplate = (id) => shiftsDictionary[id];

    /**
     * @param {BidProfile.IShiftTemplate} shiftTemplate
     * @param {string} orgJobPath
     * @returns {import('../../../../../../ZaddonsJSLibs/common-client2/typings').ShiftTooltipShiftInfo}
     */
    const generateShiftTooltipInfos = (shiftTemplate, orgJobPath) => ({
        id: shiftTemplate.id,
        timeString: timeService.calculateShiftTimes(shiftTemplate.startTime, shiftTemplate.endTime),
        segments: shiftTemplate.segments.map((x) => ({
            id: x.id,
            timeString: timeService.calculateShiftTimes(x.startTime, x.endTime),
            orgJobPath,
            segmentType: x.segmentTypeId,
            transferStrings: shiftTemplateSegmentService.generateTransferStrings(x.transferString || '', orgJobPath),
            costCenterDictionary: x.costCenterDictionary,
            skillAndCertifProfileLabels: x.segmentSCProfiles?.map((x) => x.label),
        })),
        hasTransfer: shiftTemplateSegmentService.hasTransfer(shiftTemplate.segments),
    });

    /**
     * @param {BidProfile.IScheduleTemplateItem[]} scheduleTemplateItems
     */
    const generateShiftLineRows = (scheduleTemplateItems) => {
        if (!scheduleTemplateItems) {
            return [];
        }
        return scheduleTemplateItems.map((item) => {
            const daysElement = [];
            for (let i = 0; i < item.schedulePatternTemplate.dayCount; i++) {
                const todayElements = item.schedulePatternTemplate.items
                    .filter((x) => x.day === i + 1)
                    .map((x) => {
                        const shiftTemplate = getShiftTemplate(x.shiftTemplate);
                        const {
                            name, label, startTime, endTime,
                        } = shiftTemplate;
                        let shiftName = profile.originalBidProfileInformation.scheduleSource === ScheduleSource.WorkplanManager
                            ? label
                            : name;
                        shiftName = shiftName || timeService.calculateShiftTimes(startTime, endTime);
                        return {
                            name: shiftName,
                            tooltipShiftInfo: generateShiftTooltipInfos(getShiftTemplate(x.shiftTemplate), item.orgJobPath),
                        };
                    });

                daysElement[i] = {
                    shifts: todayElements,
                };
            }

            return {
                rowName: item.schedulePatternTemplate.name,
                orgJobPath: item.orgJobPath,
                days: daysElement,
                id: item.id,
                isSelected: item.isSelected,
            };
        });
    };

    /**
      * @returns {JSX.Element}
      */
    const generateWorkplanShiftLineTable = () => {
        const scheduleTemplateTabs = [];
        for (const workplan of workplans) {
            // When source is WP, profile.scheduleTemplates.items contain workplans which each contain shiftlines
            const shiftlineRows = generateShiftLineRows(workplan.scheduleTemplates.items);
            scheduleTemplateTabs.push({
                label: workplan.name,
                content: (
                    <WeeklyShiftLineTable
                        isCheckable={!isSummary}
                        isCheckboxDisabled={isDisabled}
                        rowsProp={shiftlineRows}
                        renderEmpty={() => <EmptyTableText text={t('form.noScheduleFound')} />}
                        firstDayOfTheWeek={profile.anchorDay}
                        tableHeight={(tableHeight || 400) - heightOffset - workplanHeightOffset}
                        handleCheck={(value, checked) => handleCheck(value, checked, workplan.workplanId)}
                        handleCheckAll={(value, checked) => handleCheckAll(value, checked, workplan.workplanId)}
                        dayCount={profile.scheduleTemplates.dayCount}
                        isPeriodTypeDay={profile.scheduleTemplates.periodType === PeriodType.Days}
                        daysPerPeriod={DAY_COLUMNS_COUNT}
                        isPaginated
                    />
                ),
            });
        }

        return (
            <div className="workplan-selection-tabs-wrapper">
                <ScrollableTabsButtonAuto
                    tabs={scheduleTemplateTabs}
                    className="schedules-scrollable-tabs"
                />
            </div>
        );
    };

    /**
     * @returns {JSX.Element}
     */
    const generateCSVShiftLineTable = () => {
        // When source is CSV, profile.scheduleTemplates.items contain shiftlines
        const shiftlineRows = generateShiftLineRows(csvShiftlines);
        if (shiftlineRows.length && !profile.isUploadingSchedule) {
            return (
                <WeeklyShiftLineTable
                    isCheckable={!isSummary}
                    isCheckboxDisabled={isDisabled}
                    rowsProp={shiftlineRows}
                    firstDayOfTheWeek={profile.anchorDay}
                    renderEmpty={() => <EmptyTableText text={t('form.noScheduleFound')} />}
                    tableHeight={(tableHeight || 400) - heightOffset}
                    handleCheck={(value, checked) => handleCheck(value, checked, '')}
                    handleCheckAll={(value, checked) => handleCheckAll(value, checked, '')}
                    dayCount={profile.scheduleTemplates.dayCount}
                    isPeriodTypeDay={profile.scheduleTemplates.periodType === PeriodType.Days}
                    daysPerPeriod={DAY_COLUMNS_COUNT}
                    isPaginated
                />
            );
        }
        return <></>;
    };

    const generateShiftLineTable = () => {
        if (profile.originalBidProfileInformation.scheduleSource === ScheduleSource.WorkplanManager
            && workplans.length
            && workplans[0]?.workplanId
            && !profile.isUploadingSchedule) {
            return generateWorkplanShiftLineTable();
        }

        return generateCSVShiftLineTable();
    };

    return (
        <>
            <div
                ref={ref}
                className="table-container"
            >
                {generateShiftLineTable()}
            </div>
        </>
    );
}
export default ShiftlineTable;
